/* eslint-disable @typescript-eslint/no-explicit-any */
import { BaseFieldProps } from "./types";
import {
  TextField,
  TextAreaField,
  NumberField,
  BooleanField,
  SelectField,
  JsonField,
  ObjectField,
} from "./index";
import { IntegrationSelectorField } from "./IntegrationSelectorField";
import ScheduleBuilderField from "./ScheduleBuilderField";
import { EmailTemplateSelectorField } from "./EmailTemplateSelectorField";
import { EmailServicePlusField } from "./EmailServicePlusField";
import { AttachmentsField } from "./AttachmentsField";
import { CodeEditorField } from "./CodeEditorField";
import { evaluateVisibility } from "./utils/visibility";
import StructuredFilterField from "./StructuredFilterField";
import { FacetSelectorField } from "./FacetSelectorField";
type FieldType =
  | 'array'
  | 'string'
  | 'text'
  | 'file'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'select'
  | 'json'
  | 'object'
  | 'integrationselector'
  | 'schedule_builder'
  | 'structured_filters'
  | 'emailtemplateselector'
  | 'emailserviceplus'
  | 'attachments'
  | 'code'
  | 'facetselector';

// Utilizamos esta lista de tipos para validar os tipos de campos suportados
// e para documentação do código
const supportedFieldTypes: FieldType[] = [
  'array', 'string', 'text', 'file', 'number', 'integer',
  'boolean', 'enum', 'select', 'json', 'object', 'integrationselector',
  'schedule_builder', 'structured_filters', 'emailtemplateselector', 'emailserviceplus', 'attachments', 'code',
  'facetselector'
];

interface FieldFactoryProps extends BaseFieldProps {
  renderField?: (key: string, field: any) => React.ReactNode;
  renderArrayField?: (key: string, field: any) => React.ReactNode;
  formValues?: Record<string, any>; // Current form values for visibility evaluation
  nodeInputData?: Record<string, unknown>;
}

const fieldComponentMap: Record<string, React.FC<FieldFactoryProps>> = {
  array: ({ field, name, renderArrayField }) => renderArrayField?.(name, field),
  string: ({ field, ...props }) => field.multiline 
    ? <TextAreaField field={field} {...props} />
    : <TextField field={field} {...props} />,
  text: ({ field, ...props }) => field.multiline 
    ? <TextAreaField field={field} {...props} />
    : <TextField field={field} {...props} />,
  file: ({ field, ...props }) => <TextField field={field} {...props} />,
  number: NumberField,
  integer: NumberField,
  boolean: BooleanField,
  enum: SelectField,
  select: SelectField,
  json: JsonField,
  object: ({ field, name, control, renderField }) => (
    <ObjectField
      field={field}
      name={name}
      control={control}
      renderField={renderField}
    />
  ),
  integrationselector: IntegrationSelectorField,
  schedule_builder: ScheduleBuilderField,
  structured_filters: StructuredFilterField,
  emailtemplateselector: EmailTemplateSelectorField,
  emailserviceplus: EmailServicePlusField,
  attachments: AttachmentsField,
  code: CodeEditorField,
  facetselector: FacetSelectorField,
};

export function createField(props: FieldFactoryProps): React.ReactNode {
  const { field, formValues = {}, name } = props;
  
  if (!field) {
    console.error('Field não definido ao criar campo:', props);
    return null;
  }
  
  const fieldType = field.type?.toLowerCase();
  
  if (!fieldType) {
    console.error('Tipo de campo não definido:', field);
    return null;
  }
  
  // Check visibility conditions before rendering the field
  if (field.visibilityConditions && formValues) {
    const isVisible = evaluateVisibility(field.visibilityConditions, formValues);
    if (!isVisible) {
      // This helps prevent React from mixing up field values when visibility changes
      return (
        <div style={{ display: 'none' }} data-field-name={name} data-field-type={fieldType} data-visibility="hidden">
        </div>
      );
    }
  }
  
  if (!supportedFieldTypes.includes(fieldType as FieldType)) {
    console.warn(`Tipo de campo não suportado: ${fieldType}`);
    return null;
  }
  
  // Route 'attachments' fields by name even if type is 'array'
  const effectiveType = (fieldType === 'array' && name === 'attachments') ? 'attachments' : fieldType;

  const FieldComponent = fieldComponentMap[effectiveType];
  if (!FieldComponent) {
    console.warn(`Componente não encontrado para o tipo: ${fieldType}`);
    return null;
  }

  return <FieldComponent {...props} key={`field-${name}-${effectiveType}`} />;
}
